import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { InputControlLabel } from '@picsio/ui';

import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import localization from '../../../../shared/strings';
import Checkbox from '../../../../UIComponents/checkbox';
import Tooltip from '../../../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from '../../helpers/ReadOnlyFieldTooltip';
import parentStyles from '../../styles.module.scss';
import { aggregatedBooleanFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import {
  isAggregatedBooleanMixed,
  resolveAggregatedBooleanCheckboxValue,
} from '../helpers/resolveAggregatedBooleanCheckboxValue';

const aggregatedL10n = localization.PRODUCT_INFO.aggregated;

const AggregatedBooleanField = ({
  fieldKey,
  field,
  label,
  dialogLabel,
  readOnly,
  readOnlyTooltip,
}) => {
  const bindings = useBindAggregatedField(fieldKey, field, label, { dialogLabel });
  const checkboxValue = resolveAggregatedBooleanCheckboxValue(field);
  const isMixed = isAggregatedBooleanMixed(field);
  const isBulkField = field?.state === AGGREGATED_FIELD_STATE.MULTIPLE;
  const mixedTooltip = isMixed && !readOnly ? aggregatedL10n.multipleValues : null;

  const handleTriStateChange = useCallback(() => {
    if (readOnly) return;
    const nextValue = checkboxValue !== true;
    bindings.onChange(nextValue);
  }, [bindings, checkboxValue, readOnly]);

  if (isBulkField) {
    return (
      <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
        <div className={parentStyles.row}>
          <Tooltip content={mixedTooltip} placement="top">
            <Checkbox
              label={label}
              value={checkboxValue}
              disabled={readOnly}
              onChange={handleTriStateChange}
            />
          </Tooltip>
        </div>
      </ReadOnlyFieldTooltip>
    );
  }

  return (
    <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
      <div className={parentStyles.row}>
        <InputControlLabel
          control="checkbox"
          label={label}
          value={Boolean(checkboxValue)}
          disabled={readOnly}
          onChange={() => {
            if (readOnly) return;
            bindings.onChange(!checkboxValue);
          }}
        />
      </div>
    </ReadOnlyFieldTooltip>
  );
};

AggregatedBooleanField.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  field: aggregatedBooleanFieldPropType,
  label: PropTypes.node.isRequired,
  dialogLabel: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
};

AggregatedBooleanField.defaultProps = {
  field: null,
  dialogLabel: null,
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(AggregatedBooleanField);
